import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Paperclip, CheckCircle2, Trash2 } from "lucide-react";

const BUCKET = "security-evidence";

type EvidenceFile = { path: string; name: string; size: number; uploaded_at: string };

type Remediation = {
  id: string;
  finding_id: string;
  owner_label: string | null;
  due_date: string | null;
  status: string;
  notes: string | null;
  verification_notes: string | null;
  verified_by: string | null;
  verified_at: string | null;
  evidence: EvidenceFile[];
  updated_at: string;
};

const STATUSES = ["assigned", "in_progress", "awaiting_verification", "verified", "wont_fix"] as const;

/** Remediation workflow for a single finding: owner, due date, evidence, verification. */
export function SecurityRemediationPanel({ findingId }: { findingId: string }) {
  const [row, setRow] = useState<Remediation | null>(null);
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const [status, setStatus] = useState<string>("assigned");
  const [notes, setNotes] = useState("");
  const [verifyNotes, setVerifyNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase
      .from("security_finding_remediations")
      .select("*")
      .eq("finding_id", findingId)
      .maybeSingle();
    const r = data as unknown as Remediation | null;
    setRow(r);
    setOwner(r?.owner_label ?? "");
    setDue(r?.due_date ?? "");
    setStatus(r?.status ?? "assigned");
    setNotes(r?.notes ?? "");
    setVerifyNotes(r?.verification_notes ?? "");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingId]);

  const persist = async (patch: Partial<Remediation>) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (row) {
      const { error } = await supabase
        .from("security_finding_remediations")
        .update(patch as never)
        .eq("id", row.id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from("security_finding_remediations").insert({
        finding_id: findingId,
        owner_id: user?.id ?? null,
        created_by: user?.id ?? null,
        ...(patch as object),
      } as never);
      if (error) throw error;
    }
    await load();
  };

  const save = async () => {
    setSaving(true);
    try {
      await persist({
        owner_label: owner.trim() || null,
        due_date: due || null,
        status,
        notes: notes.trim() || null,
        verification_notes: verifyNotes.trim() || null,
      });
      toast.success("Remediation saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    if (!verifyNotes.trim()) return toast.error("Add verification notes first");
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      await persist({
        status: "verified",
        verification_notes: verifyNotes.trim(),
        verified_by: user?.id ?? null,
        verified_at: new Date().toISOString(),
      });
      toast.success("Fix verified");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Verification failed");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const path = `${findingId}/${Date.now()}-${file.name.replace(/[^\w.\-]+/g, "_")}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, file);
      if (error) throw error;
      const next: EvidenceFile[] = [
        ...(row?.evidence ?? []),
        { path, name: file.name, size: file.size, uploaded_at: new Date().toISOString() },
      ];
      await persist({ evidence: next });
      toast.success("Evidence uploaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeEvidence = async (path: string) => {
    try {
      await supabase.storage.from(BUCKET).remove([path]);
      await persist({ evidence: (row?.evidence ?? []).filter((e) => e.path !== path) });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not remove file");
    }
  };

  const openEvidence = async (path: string) => {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 120);
    if (error || !data) return toast.error(error?.message ?? "Could not open file");
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Loader2 className="size-3 animate-spin" /> Loading remediation…
      </p>
    );
  }

  const overdue = due && status !== "verified" && new Date(due) < new Date(new Date().toDateString());

  return (
    <div className="rounded-lg border border-border/60 bg-background/40 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-mono text-[10px] uppercase text-muted-foreground">Remediation</h4>
        {row?.verified_at && (
          <span className="font-mono text-[10px] uppercase text-primary flex items-center gap-1">
            <CheckCircle2 className="size-3" />
            verified {new Date(row.verified_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-2">
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Owner</span>
          <Input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="name or email"
            className="h-8 text-xs"
          />
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Due date</span>
          <Input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className={`h-8 text-xs ${overdue ? "border-destructive text-destructive" : ""}`}
          />
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Status</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>

      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Remediation plan / progress notes"
        className="text-xs"
      />

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="inline-flex">
            <input
              type="file"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload(file);
                e.target.value = "";
              }}
            />
            <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs cursor-pointer hover:bg-secondary/40">
              {uploading ? <Loader2 className="size-3 animate-spin" /> : <Paperclip className="size-3" />}
              Upload evidence
            </span>
          </label>
          <span className="font-mono text-[10px] text-muted-foreground">
            {(row?.evidence ?? []).length} file(s)
          </span>
        </div>
        {(row?.evidence ?? []).map((f) => (
          <div key={f.path} className="flex items-center justify-between gap-2 text-xs">
            <button
              type="button"
              onClick={() => openEvidence(f.path)}
              className="truncate text-left underline decoration-dotted hover:text-primary"
            >
              {f.name}
            </button>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-mono text-[10px] text-muted-foreground">
                {Math.max(1, Math.round(f.size / 1024))} KB
              </span>
              <Button size="sm" variant="ghost" onClick={() => removeEvidence(f.path)}>
                <Trash2 className="size-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Textarea
        value={verifyNotes}
        onChange={(e) => setVerifyNotes(e.target.value)}
        placeholder="Verification notes (how the fix was confirmed)"
        className="text-xs"
      />

      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving}>
          {saving && <Loader2 className="size-3 animate-spin mr-1" />}
          Save remediation
        </Button>
        <Button size="sm" variant="outline" onClick={verify} disabled={saving}>
          <CheckCircle2 className="size-3 mr-1" />
          Verify fix
        </Button>
      </div>
    </div>
  );
}
